import React, { useEffect, useState } from "react";
import {
  LuSparkles,
  LuBell,
  LuCross,
  LuTruck,
  LuHelpingHand,
  LuSiren,
  LuQrCode,
  LuMapPin,
  LuHeartPulse,
  LuScanLine,
  LuShieldCheck,
  LuBuilding2,
  LuZap,
  LuHeart,
  LuPhoneCall,
} from "react-icons/lu";
import MarketingLayout from "../components/site/MarketingLayout";
import PrimaryButton from "../components/site/PrimaryButton";
import Reveal from "../components/site/Reveal";
import AerMark, { highlightAer } from "../components/site/AerMark";
import GoldenHourMark, {
  highlightGoldenHour,
} from "../components/site/GoldenHourMark";
import LifesavrMark, {
  highlightLifesavr,
} from "../components/site/LifesavrMark";
import QrifyMark, { highlightQrify } from "../components/site/QrifyMark";

const flow = [
  { label: "Call", Icon: LuPhoneCall },
  { label: "Scan", Icon: LuScanLine },
  { label: "Alert", Icon: LuBell },
  { label: "Locate", Icon: LuMapPin },
  { label: "Dispatch", Icon: LuTruck },
  { label: "Care", Icon: LuHeartPulse },
];

const pillars = [
  {
    title: "One-Click Call",
    description:
      "With a single click, a victim can instantly activate an Emergency Call, alerting the appropriate emergency response network to ensure rapid assistance.",
    Icon: LuPhoneCall,
  },
  {
    title: "Unique QR identity",
    description:
      highlightQrify(
        "A personal QRify code that connects bystanders to the right emergency network in One Scan when the victim is unresponsive."
      ),
    Icon: LuQrCode,
  },
  {
    title: "Real-time coordination",
    description:
      "It alerts Emergency Contacts, Ambulances and Hospitals together so response starts without delay.",
    Icon: LuBell,
  },
  {
    title: "Faster outcomes",
    description:
      "Precise location and pre-arranged dispatch cut negotiations and wasted minutes.",
    Icon: LuShieldCheck,
  },
];

const why = [
  {
    title: "Instant Alert System",
    description:
      "With real-time notifications, our system immediately sends accident data to emergency centres and contacts, reducing response time.",
    Icon: LuBell,
  },
  {
    title: "Healthcare Connection",
    description:
      "Links with nearby specialised hospitals for prompt treatment and facilitates seamless communication between emergency services.",
    Icon: LuCross,
  },
  {
    title: "Hassle-Free Dispatch",
    description:
      "Streamlined dispatch eliminates delays and saves time by avoiding unnecessary negotiations with the ambulance providers during emergencies.",
    Icon: LuTruck,
  },
];

const workflowStepImages = {
  scan: "/images/workflow-step-scan.png",
  call: "/images/workflow-step-call.png",
  alerts: "/images/workflow-step-alerts.png",
  location: "/images/workflow-step-location.png",
  ambulance: "/images/workflow-step-ambulance.png",
  hospital: "/images/workflow-step-hospital.png",
};

const priyaSteps = [
  {
    title: "One-Click Call",
    description: highlightQrify(
      highlightGoldenHour(
        highlightAer(
          "A single click activates QRify's Accelerated Emergency Response, instantly alerting emergency contacts and responders while sharing the victim's live location—all within the Golden Hour."
        )
      )
    ),
    Icon: LuPhoneCall,
    image: workflowStepImages.call,
    accent: "blue",
  },
  {
    title: "Immediate Alerts",
    description:
      highlightQrify(
        "QRify sends instant notifications to Priya’s Emergency Contacts and kickstarts local emergency services, dispatching an Ambulance right away."
      ),
    Icon: LuBell,
    image: workflowStepImages.alerts,
    accent: "red",
  },
  {
    title: "Exact Location Sharing",
    description:
      "Location of the accident site is instantly captured and shared with Priya's family, friends and emergency responders, ensuring they can reach her quickly and efficiently.",
    Icon: LuMapPin,
    image: workflowStepImages.location,
    accent: "green",
  },
  {
    title: "Streamlined Ambulance Service",
    description:
      "The solution ensures a smooth connection with emergency responders, avoiding negotiations and cancellations and facilitating prompt, guaranteed service.",
    Icon: LuTruck,
    image: workflowStepImages.ambulance,
    accent: "blue",
  },
  {
    title: "Seamless Hospital Coordination",
    description:
      highlightQrify(
        "QRify coordinates with the Ambulance & Hospital in advance, ensuring that the victim is taken to the nearest medical facility and is provided Emergency Room treatment."
      ),
    Icon: LuBuilding2,
    image: workflowStepImages.hospital,
    accent: "purple",
  },
];

const mohanSteps = [
  {
    title: "Instant Scan",
    description: highlightQrify(
      highlightAer(
        "A colleague quickly scans Mohan’s QRify code on his ID badge, instantly activating the Accelerated Emergency Response protocol."
      )
    ),
    Icon: LuScanLine,
    image: workflowStepImages.scan,
    accent: "blue",
  },
  {
    title: "Real-Time Alerts",
    description:
      highlightQrify(
        "QRify instantly alerts emergency contacts and emergency services, initiating an accelerated response by dispatching an ambulance without delay."
      ),
    Icon: LuBell,
    image: workflowStepImages.alerts,
    accent: "red",
  },
  {
    title: "Precise Location",
    description:
      "Mohan’s exact office location is shared with emergency responders, guiding them directly to him.",
    Icon: LuMapPin,
    image: workflowStepImages.location,
    accent: "green",
  },
  {
    title: "Effortless Ambulance Coordination",
    description:
      highlightQrify(
        "QRify ensures seamless coordination with ambulance services, eliminating delays, negotiations and cancellations to deliver prompt and reliable emergency transport."
      ),
    Icon: LuTruck,
    image: workflowStepImages.ambulance,
    accent: "blue",
  },
  {
    title: "Seamless Hospital Coordination",
    description:
      highlightQrify(
        "QRify seamlessly coordinates with ambulance services and hospitals, ensuring the victim reaches the nearest medical facility where emergency care is ready upon arrival."
      ),
    Icon: LuBuilding2,
    image: workflowStepImages.hospital,
    accent: "purple",
  },
];

const workflowBenefits = [
  {
    title: "Seconds Matter",
    description: "Accelerated response can save more lives.",
    Icon: LuZap,
  },
  {
    title: "Trusted Network",
    description: "Verified responders and partners.",
    Icon: LuShieldCheck,
  },
  {
    title: "Live Tracking",
    description: "Real-time updates every step of the way.",
    Icon: LuMapPin,
  },
  {
    title: "Better Outcomes",
    description: "Timely care leads to better recovery.",
    Icon: LuHelpingHand,
  },
];

const WorkflowSection = ({
  steps,
  eyebrow,
  title,
  titleAccent,
  titleSuffix = "",
  subtitle,
  tone = "default",
}) => (
  <div className={`pd-workflow pd-workflow--${tone}`}>
    <div className="pd-workflow__panel">
      <div className="pd-workflow__head">
        <span className="pd-workflow__eyebrow">
          <LuShieldCheck size={14} strokeWidth={2.25} aria-hidden="true" />
          {eyebrow}
        </span>
        <h2 className="pd-workflow__title">
          {title}{" "}
          <span className="pd-workflow__title-accent">{titleAccent}</span>
          {titleSuffix ? ` ${titleSuffix}` : ""}
        </h2>
        <p className="pd-workflow__subtitle">{subtitle}</p>
      </div>

      <div className="pd-workflow__track" role="list">
        {steps.map((step, i) => {
          const Icon = step.Icon;
          return (
            <Reveal key={step.title} delayClass={`sv-d${(i % 4) + 1}`}>
              <article
                className={`pd-workflow__step pd-workflow__step--${step.accent || "blue"}`}
                role="listitem"
              >
                <div className="pd-workflow__step-icon" aria-hidden="true">
                  {step.image ? (
                    <img
                      src={step.image}
                      alt=""
                      className="pd-workflow__step-img"
                    />
                  ) : (
                    <Icon size={28} strokeWidth={1.85} />
                  )}
                </div>
                <h3 className="pd-workflow__step-title">{step.title}</h3>
                <p className="pd-workflow__step-text">{step.description}</p>
                <span className="pd-workflow__step-bar" aria-hidden="true" />
              </article>
            </Reveal>
          );
        })}
      </div>

      <div className="pd-workflow__benefits">
        {workflowBenefits.map((item, i) => {
          const Icon = item.Icon;
          return (
            <div key={item.title} className="pd-workflow__benefit">
              <span className="pd-workflow__benefit-icon" aria-hidden="true">
                <Icon size={20} strokeWidth={2} />
              </span>
              <div>
                <strong>{item.title}</strong>
                <p>{item.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </div>
);

const OutcomeBanner = ({
  variant = "patient",
  eyebrow = "Peace of Mind, Always",
  titleBefore = "Every",
  titleAccent = "Life",
  titleAfter = "Secured",
  description,
  image,
  imageAlt,
  badgeTitle = "You're in",
  badgeSubtitle = "Safe Hands",
}) => (
  <article className={`pd-outcome pd-outcome--${variant}`}>
    {variant === "family" ? (
      <span className="pd-outcome__waves" aria-hidden="true" />
    ) : null}
    <div className="pd-outcome__copy">
      <span className="pd-outcome__eyebrow">
        <LuShieldCheck size={14} strokeWidth={2.5} aria-hidden="true" />
        {eyebrow}
      </span>
      <h3 className="pd-outcome__title">
        {titleBefore}{" "}
        <span className="pd-outcome__title-accent">{titleAccent}</span>{" "}
        {titleAfter}
      </h3>
      <span className="pd-outcome__rule" aria-hidden="true" />
      <p className="pd-outcome__text">{description}</p>
    </div>

    <div className="pd-outcome__visual">
      {variant === "family" ? (
        <>
          <span className="pd-outcome__heart pd-outcome__heart--1" aria-hidden="true">
            <LuHeart size={20} strokeWidth={2} fill="currentColor" />
          </span>
          <span className="pd-outcome__heart pd-outcome__heart--2" aria-hidden="true">
            <LuHeart size={14} strokeWidth={2} fill="currentColor" />
          </span>
          <span className="pd-outcome__glow-path" aria-hidden="true" />
          <figure className="pd-outcome__photo pd-outcome__photo--family">
            <img src={image} alt={imageAlt} />
          </figure>
        </>
      ) : (
        <>
          <span className="pd-outcome__dots" aria-hidden="true" />
          <span className="pd-outcome__swirl" aria-hidden="true" />
          <span className="pd-outcome__spark pd-outcome__spark--1" aria-hidden="true" />
          <span className="pd-outcome__spark pd-outcome__spark--2" aria-hidden="true" />
          <figure className="pd-outcome__photo">
            <img src={image} alt={imageAlt} />
          </figure>
          <div className="pd-outcome__badge">
            <span className="pd-outcome__badge-icon" aria-hidden="true">
              <LuShieldCheck size={22} strokeWidth={2.4} />
            </span>
            <strong>
              {badgeTitle}
              <br />
              {badgeSubtitle}
            </strong>
          </div>
        </>
      )}
    </div>
  </article>
);

const Product = () => {
  const [activeFlow, setActiveFlow] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setActiveFlow((prev) => (prev + 1) % flow.length);
    }, 2200);
    return () => window.clearInterval(id);
  }, []);

  return (
    <MarketingLayout>
      <section className="hm-hero pd-hero">
        <div className="hm-hero__orbs" aria-hidden="true">
          <span className="hm-orb hm-orb--1" />
          <span className="hm-orb hm-orb--2" />
          <span className="hm-orb hm-orb--3" />
        </div>
        <div className="sv-container hm-hero__grid">
          <div className="hm-hero__copy">
            <span className="sv-eyebrow hm-hero__chip">
              <span className="hm-hero__chip-icon" aria-hidden="true">
                <LuSparkles size={14} strokeWidth={2.25} />
              </span>
              <QrifyMark /> <AerMark />
            </span>
            <h1 className="sv-h1 hm-hero__title">
              What If Emergency Response Could{" "}
              <span className="sv-accent">Be Smarter?</span>
            </h1>
            <p className="sv-lead hm-hero__lead">
              One click/scan connects to Emergency Contacts, Ambulances and Hospitals —
              delivering rapid response when{" "}
              <span className="sv-urgent">Every Second Counts</span>.
            </p>
            <div className="hm-hero__actions">
              <PrimaryButton to="/contact" variant="ghost-light" pulse>
                Contact Us
              </PrimaryButton>
            </div>
            <ul className="pd-flow" aria-label="QRify response flow">
              {flow.map(({ label, Icon }, i) => (
                <li
                  key={label}
                  className={`pd-flow__step${activeFlow === i ? " is-active" : ""}`}
                  onMouseEnter={() => setActiveFlow(i)}
                >
                  <span className="pd-flow__icon" aria-hidden="true">
                    <Icon size={16} strokeWidth={2.2} />
                  </span>
                  <span>{label}</span>
                  {i < flow.length - 1 ? (
                    <span className="pd-flow__connector" aria-hidden="true" />
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
          <div className="hm-hero__visual">
            <div className="hm-hero__ring" aria-hidden="true" />
            <div className="hm-hero__ring hm-hero__ring--2" aria-hidden="true" />
            <span className="pd-hero__scan" aria-hidden="true" />
            <figure className="hm-hero__photo hm-hero__photo--full">
              <img
                src="/images/qrify-product-hero.png"
                alt="3D character holding a smartphone showing the QRify Accelerated Emergency Response QR code"
              />
              <figcaption className="hm-hero__photo-caption">
                <span>Call · Scan · Alert · Dispatch</span>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="sv-section pd-about">
        <div className="sv-container">
          <Reveal>
            <div className="sv-section-head pd-about__head">

              <h2 className="sv-h2">
                About <span className="sv-navy"><QrifyMark /></span>
              </h2>
              <p className="sv-lead">
                <QrifyMark /> is a one-of-a-kind <AerMark /> tool designed to save
                lives. By using unique QR codes, it links{" "}
                <LifesavrMark>Lifesavrs</LifesavrMark> to the
                patient’s Emergency Contacts, Ambulances and Hospitals. In a
                crisis, <QrifyMark /> ensures rapid coordination between emergency
                services and the victim — improving outcomes and reducing delays.
              </p>
            </div>
          </Reveal>
          <div className="pd-pillars">
            {pillars.map((item, i) => {
              const Icon = item.Icon;
              return (
                <Reveal key={item.title} delayClass={`sv-d${i + 1}`}>
                  <article className="pd-pillar">
                    <div className="pd-pillar__icon" aria-hidden="true">
                      <Icon size={22} strokeWidth={2} />
                    </div>
                    <h3 className="sv-h3">{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
          <Reveal>
            <aside className="pd-disclaimer">
              <LuShieldCheck size={22} strokeWidth={1.9} aria-hidden="true" />
              <div>
                <h2 className="sv-h3">Disclaimer</h2>
                <p className="sv-body">
                  {highlightGoldenHour(
                    'In the event of a Medical Emergency, the victim will be taken to the nearest Hospital for Emergency Room Treatment. Should they require further treatment, it becomes the responsibility of the patient and their friends/ family. We are an Emergency Response System trying to save lives during the crucial "Golden Hour."'
                  )}
                </p>
              </div>
            </aside>
          </Reveal>
        </div>
      </section>

      <section className="sv-section">
        <div className="sv-container">
          <Reveal>
            <div className="sv-section-head">

              <h2 className="sv-h2">
                Why <span className="sv-navy"><QrifyMark /> Matters</span>
              </h2>
              <p className="sv-lead">
                Built for moments when hesitation costs lives — on roads, at
                work and everywhere emergencies strike.
              </p>
            </div>
          </Reveal>
          <div className="hm-ai pd-why">
            {why.map((item, i) => {
              const Icon = item.Icon;
              return (
                <Reveal key={item.title} delayClass={`sv-d${i + 1}`}>
                  <article className="hm-ai__card pd-why__card">
                    <div className="hm-ai__icon">
                      <Icon size={24} />
                    </div>
                    <h3 className="hm-ai__title">{item.title}</h3>
                    <p>{item.description}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="sv-section">
        <div className="sv-container pd-journey">
          <Reveal>
            <div className="pd-journey__label">
              <p className="pd-journey__kicker">Priya’s journey</p>
            </div>
          </Reveal>

          <Reveal>
            <div className="pd-journey__card">
              <article className="hm-story pd-journey__story">
                <div className="hm-story__copy">
                  <span className="sv-eyebrow hm-story__badge">
                    <span className="hm-story__badge-dot" aria-hidden="true" />
                    <LuHelpingHand
                      size={15}
                      strokeWidth={2.25}
                      aria-hidden="true"
                    />
                    <LifesavrMark>Lifesavr’s</LifesavrMark> Swift Aid
                  </span>
                  <h2 className="sv-h2">Quick Response to Accidents</h2>
                  <p className="sv-body">
                    Imagine Priya is involved in a serious car accident. With a
                    single click, she activates <QrifyMark />'s <AerMark /> System. Her
                    location is instantly shared with emergency contacts and
                    emergency responders, while concurrent alerts initiate a
                    coordinated response. This seamless activation ensures
                    life-saving assistance reaches her within the{" "}
                    <GoldenHourMark />, when Every Second Counts.
                  </p>
                  <PrimaryButton to="/contact" variant="ghost">
                    Contact Us
                  </PrimaryButton>
                </div>
                <div className="hm-story__visual hm-story__visual--photo">
                  <img
                    src="/images/product-relieved-indian-patient.png"
                    alt="Indian woman resting safely in hospital after emergency care, looking relieved"
                  />
                </div>
              </article>

              <WorkflowSection
                steps={priyaSteps}
                eyebrow={highlightQrify("How QRify Works")}
                title="From Click to"
                titleAccent="Care"
                titleSuffix="in Minutes"
                subtitle={highlightQrify(
                  highlightLifesavr(
                    "QRify connects Lifesavrs, responders and hospitals in real-time to ensure faster help and better outcomes."
                  )
                )}
              />

              <OutcomeBanner
                description={highlightQrify(
                  highlightGoldenHour(
                    "With Svasth’s QRify, Priya gets fast, effective care right after her accident within the Golden Hour. Quick alerts and actions ensure Priya's life is saved, bringing comfort to her loved ones."
                  )
                )}
                image="/images/outcome-priya-safe.png"
                imageAlt="Priya smiling and recovering safely after emergency care"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sv-section sv-section--soft">
        <div className="sv-container pd-journey">
          <Reveal>
            <div className="pd-journey__label">
              <p className="pd-journey__kicker">Mohan’s journey</p>
            </div>
          </Reveal>

          <Reveal>
            <div className="pd-journey__card pd-journey__card--urgent">
              <article className="hm-story hm-story--flip pd-journey__story">
                <div className="hm-story__visual hm-story__visual--photo">
                  <img
                    src="/images/story-critical-illness-home.png"
                    alt="Older office worker Mohan clutching his chest in distress while a colleague calls for emergency help"
                  />
                </div>
                <div className="hm-story__copy">
                  <span className="sv-eyebrow hm-story__badge hm-story__badge--urgent">
                    <span className="hm-story__badge-dot" aria-hidden="true" />
                    <LuSiren size={15} strokeWidth={2.25} aria-hidden="true" />
                    Urgent Care Delivered
                  </span>
                  <h2 className="sv-h2">
                    <AerMark /> for a Sudden Heart Attack
                  </h2>
                  <p className="sv-body">
                    Imagine Mohan, a 60-year-old office worker, suddenly
                    collapsing at work due to his history of heart ailment. His
                    colleagues take quick action to ensure Mohan receives the
                    urgent care he needs.
                  </p>
                  <PrimaryButton to="/contact">Contact us</PrimaryButton>
                </div>
              </article>

              <WorkflowSection
                steps={mohanSteps}
                eyebrow={highlightQrify("How QRify Works")}
                title="From Scan to"
                titleAccent="Save"
                titleSuffix="in Minutes"
                subtitle={highlightQrify(
                  highlightGoldenHour(
                    "When a workplace emergency occurs, QRify concurrently alerts emergency contacts and coordinates with ambulance services and hospitals, ensuring life-saving assistance is delivered within the Golden Hour."
                  )
                )}
                tone="urgent"
              />

              <OutcomeBanner
                variant="family"
                description={highlightQrify(
                  highlightGoldenHour(
                    "With Svasth’s QRify, Mohan receives fast, effective cardiac care thanks to his colleagues’ swift action. Accelerated Response and Smooth Coordination saves Mohan's life within the Golden Hour, bringing relief to his loved ones."
                  )
                )}
                image="/images/outcome-mohan-family.png"
                imageAlt="Mohan surrounded by loving family, relieved and safe after cardiac care"
              />
            </div>
          </Reveal>
        </div>
      </section>

    </MarketingLayout>
  );
};

export default Product;
